import { apiFetch } from '@/lib/apiClient';
import type { OfferDeletionRequest, OfferDeletionRequestStatus } from '@coreltb/shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const keys = {
  all: ['offer-deletion-requests'] as const,
  list: (status?: OfferDeletionRequestStatus) => [...keys.all, 'list', status] as const,
  byOffer: (offerId: string) => [...keys.all, 'by-offer', offerId] as const,
};

export interface CreateOfferDeletionRequestInput {
  offerId: string;
  reason: string;
}

/**
 * POST — handlowiec prosi o usuniecie oferty (soft delete po approve admina).
 * Backend zwraca 409 jezeli juz pending dla oferty lub oferta soft-deleted.
 */
export function useCreateOfferDeletionRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOfferDeletionRequestInput) =>
      apiFetch<{ id: string; status: 'pending' }>('/api/offer-deletion-requests', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

/**
 * Lista request'ow — widziana przez admin/dyrektor/developer.
 * Polling 30s dla badge'a w sidebarze.
 */
export function useOfferDeletionRequestsList(status?: OfferDeletionRequestStatus) {
  return useQuery({
    queryKey: keys.list(status),
    queryFn: () => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      return apiFetch<OfferDeletionRequest[]>(`/api/offer-deletion-requests${qs}`);
    },
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
}

/**
 * Request'y dla konkretnej oferty — polling 5s. Po approved → invalidate
 * offers list (oferta znika).
 */
export function useOfferDeletionRequestsByOffer(offerId: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: keys.byOffer(offerId ?? ''),
    queryFn: async () => {
      const data = await apiFetch<
        Array<{
          id: string;
          offerId: string;
          reason: string;
          status: OfferDeletionRequestStatus;
          createdBy: string;
          reviewedBy: string | null;
          reviewComment: string | null;
          createdAt: number;
          reviewedAt: number | null;
        }>
      >(`/api/offer-deletion-requests/by-offer/${offerId}`);

      // Po approved oferta jest soft-deleted — invalidate offer detail + list
      if (offerId && data.some((r) => r.status === 'approved')) {
        qc.invalidateQueries({ queryKey: ['offers'] });
      }
      return data;
    },
    enabled: Boolean(offerId),
    staleTime: 5 * 1000,
    refetchInterval: 5 * 1000,
  });
}

export interface ReviewOfferDeletionRequestInput {
  id: string;
  status: 'approved' | 'rejected';
  reviewComment?: string;
}

export function useReviewOfferDeletionRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReviewOfferDeletionRequestInput) =>
      apiFetch<{ id: string; status: OfferDeletionRequestStatus }>(
        `/api/offer-deletion-requests/${input.id}`,
        {
          method: 'PATCH',
          body: { status: input.status, reviewComment: input.reviewComment },
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      // Approved → invalidate offers cache (oferta znika z listy)
      qc.invalidateQueries({ queryKey: ['offers'] });
    },
  });
}
